<template>
  <div
    id="pitch-types"
    class="pitchtypes-section campsite-subsection"
    :class="{ desktop: $isDesktop }"
    data-pitchtypes-scroll-target
    data-campsite-subsection
  >
    <AsyncEditSearchButton
      v-if="shouldShowEditSearchButton && !hasAvailability"
      :key="getAsyncComponentKey('editSearchButton1')"
      v-bind="editSearchButtonProps"
      @vue:mounted="setAsyncComponentKey('editSearchButton1')"
    />

    <!-- <async-component-skeleton :skeleton-height="15 * pitchtypes.length + 'em'"> -->
    <AsyncPitchTypesSection
      :key="getAsyncComponentKey('pitchTypesSection')"
      :pitchtypes="pitchtypes"
      :selected-types="selectedTypes"
      :has-charge-types="hasChargeTypes"
      :selected-facets="selectedFacets"
      :charge-types-updating="chargeTypesUpdating"
      :party="party"
      @vue:mounted="onPitchTypesSectionMounted"
    >
      <template v-if="$isDesktop" #pre-pitch-types-list>
        <div v-if="!isDated && !availableToSearch" class="zero-availability">
          <translate class="zero-availability-msg">
            No availability at present on Pitchup.com
          </translate>
          <i18n-t
            keypath="{chooseYourDates} and we'll email when available."
            tag="p"
          >
            <template #chooseYourDates>
              <translate>Choose your dates</translate>
            </template>
          </i18n-t>
          <a
            v-scrollto="{
              target: '#nearby-bookable',
              options: { duration: 250, offset: -30 },
            }"
          >
            <translate>See nearby bookable sites</translate>
          </a>
        </div>

        <lazy-search-bar-desktop
          :key="`search-bar-${$route.name}`"
          v-scrollto="{
            target: '#pitch-types',
            options: { duration: 200, offset: -75 },
          }"
          :campsite-id="campsiteId"
          :campsite-opening-date-ranges="campsiteOpeningDateRanges"
          :campsite-pitch-category-ids="campsitePitchCategoryIds"
          :campsite-categories="campsiteCategories"
          :campsite-slug="campsiteSlug"
          :campsite-hierarchy-path="campsiteHierarchyPath"
          :current-pitchtype-id="currentPitchtypeId"
          no-location
          clear-dates-update
        >
          <translate
            v-if="hasChildren && campsiteAdultsOnly"
            class="campsite-party-note adults-only"
            :tt="{ minAdultAge: campsiteMinAdultAge }"
          >
            This listing accepts adults only (defined as age {minAdultAge}+)
          </translate>
          <lazy-campsite-party
            v-else-if="hasChildren"
            :party="party"
            :min-adult-age="campsiteMinAdultAge"
            class="campsite-party-note"
          />
        </lazy-search-bar-desktop>
      </template>
      <template #card="{ pitchtype, pitchtypeIndex }">
        <lazy-pitch-type-card-dated
          v-if="hasChargeTypes"
          :available-to-search="availableToSearch"
          :campsite-name="campsiteName"
          :campsite-slug="campsiteSlug"
          :campsite-categories="campsiteCategories"
          :campsite-hierarchy-path="campsiteHierarchyPath"
          :campsite-super-facilities="campsiteSuperFacilities"
          :campsite-allow-override-immediate="campsiteAllowOverrideImmediate"
          :pitchtype="pitchtype"
          :party="party"
          :date-range="dateRange"
          :campsite-payment-timing="campsitePaymentTiming"
          :campsite-refund-policy="campsiteRefundPolicy"
          :campsite-useful-info="campsiteUsefulInfo"
          :campsite-terms-useful-info="campsiteTermsUsefulInfo"
          :calendar-start-date="calendarStartDate"
        />

        <lazy-pitch-type-card-non-dated
          v-else
          :available-to-search="availableToSearch"
          :campsite-name="campsiteName"
          :campsite-super-facilities="campsiteSuperFacilities"
          :campsite-categories="campsiteCategories"
          :campsite-useful-info="campsiteUsefulInfo"
          :campsite-terms-useful-info="campsiteTermsUsefulInfo"
          :campsite-hierarchy-path="campsiteHierarchyPath"
          :campsite-slug="campsiteSlug"
          :campsite-adults-only="campsiteAdultsOnly"
          :pitchtype="pitchtype"
          :pitchtype-index="pitchtypeIndex"
          :party="party"
          :calendar-start-date="calendarStartDate"
        />
      </template>
    </AsyncPitchTypesSection>
    <!-- </async-component-skeleton> -->

    <AsyncEditSearchButton
      v-if="shouldShowEditSearchButton && hasAvailability"
      :key="getAsyncComponentKey('editSearchButton2')"
      v-bind="editSearchButtonProps"
      @vue:mounted="setAsyncComponentKey('editSearchButton2')"
    />
  </div>
</template>

<script lang="ts">
import { defineAsyncComponent } from 'vue'
import { hydrateOnVisibleSafe } from '../../../utility/hydration/hydrateOnVisibleSafe'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import type { CampsiteCategory } from '~/utility/types/CampsiteCategory'
import { logger } from '../../../utility/logger'
import {
  CampsiteFacility,
  CampsiteOpeningDateRange,
  PaymentTiming,
  PitchType,
} from '../../campsite-details/types'
import { Party, PitchTypeWithChargeType } from '../types'

interface EditSearchButtonProps {
  dateRange: { start: string; end: string }
  party: Party
  minAdultAge: number
  campsiteSlug: string
}

const AsyncPitchTypesSection = defineAsyncComponent({
  loader: () => import('./PitchTypesSection.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

const AsyncEditSearchButton = defineAsyncComponent({
  loader: () => import('./EditSearchButton.vue'),
  hydrate: hydrateOnVisibleSafe(),
})

@Component({
  components: { AsyncPitchTypesSection, AsyncEditSearchButton },
})
class PitchTypesGroup extends Vue {
  @Prop({ required: true })
  campsiteId: string

  @Prop({ required: true })
  campsiteCategories: CampsiteCategory[]

  @Prop({ required: true })
  campsiteName: string

  @Prop({ required: true })
  campsiteSlug: string

  @Prop({ required: true })
  campsiteHierarchyPath: string

  @Prop({ required: true })
  campsiteSuperFacilities: CampsiteFacility[]

  @Prop({ required: true })
  campsiteAllowOverrideImmediate: boolean

  @Prop({ required: true })
  campsitePaymentTiming: PaymentTiming

  @Prop({ required: true })
  campsiteRefundPolicy: string

  @Prop({ required: true })
  campsiteUsefulInfo: string

  @Prop({ required: true })
  campsiteTermsUsefulInfo: string[]

  @Prop({ required: true })
  campsiteMinAdultAge: number

  @Prop({ default: false })
  campsiteAdultsOnly: boolean

  @Prop({ required: false })
  chargeTypesUpdating: boolean

  @Prop()
  campsiteOpeningDateRanges?: CampsiteOpeningDateRange[]

  @Prop()
  campsitePitchCategoryIds?: number[]

  @Prop({ required: true })
  hasAvailability: boolean | null

  @Prop({ default: true })
  availableToSearch: boolean

  @Prop({ required: true })
  pitchtypes: Array<PitchType | PitchTypeWithChargeType>

  @Prop({ required: false })
  currentPitchtypeId?: string

  @Prop({ required: true })
  party: Party

  @Prop({ required: true })
  dateRange: { start: string | null; end: string | null }

  @Prop({ required: true })
  selectedTypes: number[]

  @Prop()
  calendarStartDate: string

  @Prop()
  selectedFacets: string[]

  asyncComponentKeys: Record<string, string> = {}

  // Helper methods for async component keys
  getAsyncComponentKey(componentName: string): string {
    return (
      this.asyncComponentKeys[componentName] ||
      `${this.$route.fullPath.split('#')[0]}-${componentName}`
    )
  }

  setAsyncComponentKey(componentName: string): void {
    if (this.$.isUnmounted) return
    this.asyncComponentKeys[componentName] =
      `${this.$route.fullPath.split('#')[0]}-${componentName}`
  }

  onPitchTypesSectionMounted(): void {
    if (this.$.isUnmounted) return
    this.setAsyncComponentKey('pitchTypesSection')
    this.$emit('pitch-type-section-mounted')
  }

  mounted() {
    if (this.$route.hash === '#pitch-types') {
      logger('Scroll to PitchTypesGroup el')
      setTimeout(() => {
        if (!this.$.isUnmounted && this.$el) {
          this.$el.scrollIntoView()
        }
      }, 250)
    }
  }

  isCurrentPitchtype(id: string) {
    return id === this.currentPitchtypeId
  }

  get isDated() {
    return !!this.$route.query?.arrive
  }

  get hasChildren() {
    return this.party.childAges.length > 0
  }

  get hasChargeTypes() {
    return this.pitchtypes.length ? 'chargetype' in this.pitchtypes[0] : false
  }

  get shouldShowEditSearchButton() {
    return !this.currentPitchtypeId && this.hasChargeTypes && !this.$isDesktop
  }

  get editSearchButtonProps(): Partial<EditSearchButtonProps> {
    return {
      party: this.party,
      dateRange: this.dateRange as { start: string; end: string },
      minAdultAge: this.campsiteMinAdultAge,
      campsiteSlug: this.campsiteSlug,
    }
  }
}

export default toNative(PitchTypesGroup)
</script>
<style scoped>
.pitchtypes-section {
  background: var(--colour-page-background);
  padding-bottom: 0.8em;
}

.pitchtypes-section :deep(.pt-status-msg) {
  align-items: center;
  background-color: #000;
  border-radius: 1em;
  color: white;
  display: flex;
  flex: 0 1 auto;
  font-size: var(--small);
  font-weight: bold;
  justify-items: flex-start;
  line-height: 1;
  margin: 0 -0.2em 0 0;
  padding: 0.4em 0.6em;
}

.pitchtypes-section :deep(.pt-status-msg img) {
  align-self: flex-start;
  height: 1.1em;
  margin-right: 0.3em;
  width: auto;
}

.pitchtypes-section :deep(.pt-status-msg.other-dates) {
  background-color: var(--colour-price);
}

/* desktop & tablet */
.pitchtypes-section.desktop {
  background: var(--colour-page-background-light);
}

.pitchtypes-section.desktop :deep(.ct-status-msg) {
  margin: 0 0 1.4em 0;
}

.desktop .zero-availability {
  border-radius: 1em;
  background: white;
  padding: 1em;
  margin: auto 0 2em;
}

.desktop .zero-availability p {
  margin-top: 0.75em;
}

.desktop .campsite-party-note {
  flex-basis: 100%;
  order: 6;
  font-size: var(--body);
  color: var(--colour-dark-blue);
  display: block;
  margin: auto 0 -0.5em 0;
  padding-left: 0.25em;
  display: none;
}

.desktop .campsite-party-note.adults-only,
.desktop .campsite-party-note.with-converted-child-ages :deep(.converted-ages) {
  font-weight: bold;
}

.desktop .campsite-party-note.adults-only,
.desktop .campsite-party-note.with-converted-child-ages {
  display: block;
}
</style>
